import cx from 'classnames';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AlloyButton, StickySection } from 'cat-ecommerce-alloy';
import { useBreakpoint } from '@app/hooks';
import { PAGE_BREAKPOINTS, STATUS } from '@app/constants/commonConstants';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import styles from './styles.module.scss';

const QuickOrderActionButtons = ({
  showAddToCartCheckout,
  onAddToCartClick
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const {
    errors: { items },
    touched,
    values,
    ...formikBag
  } = useFormikContext();

  const error = useSelector(s => s.errors?.quickOrder?.main?.message);
  const quantityError = error === t('QO_MAXIMUM_QUANTITY_UPDATED_ERROR');

  const hasErrors =
    !!items?.find(
      (item, index) =>
        ((item?.quantity && values.items?.[index]?.itemNumber) ||
          (item?.itemNumber && values.items?.[index]?.quantity)) &&
        touched.items?.[index]
    ) || quantityError;

  const isFormEmpty = !values.items?.some(
    item => item.quantity && item.itemNumber
  );

  const { getCurrentOrderStatus } = useSelector(s => s.orders);
  const getCurrentOrderLoading =
    getCurrentOrderStatus === STATUS.IDLE ||
    getCurrentOrderStatus === STATUS.PENDING;
  const {
    isRequiredFieldsLoading,
    addToCartSubmitStatus,
    cartFormSubmitStatus
  } = useSelector(s => s.checkout);
  const { isOrderPriceAvailabilityLoading } = useSelector(s => s.orders);
  const addToCartBtnLoading = addToCartSubmitStatus === STATUS.PENDING;
  const cartFormSubmitBtnLoading = cartFormSubmitStatus === STATUS.PENDING;
  const isBuyOnBehalf = useSelector(s => s.common.isBuyOnBehalf);
  const isPCCCheckoutForCSR =
    useSelector(s => s.featureFlag?.PCCCheckoutForCSR) ?? true;
  const isCheckoutForCSRDisabled = isBuyOnBehalf && !isPCCCheckoutForCSR;
  const requestByDateEnabled = useSelector(
    state => state.orders.requestByDate?.requestByDateEnabled
  );
  const currentOrderId = useSelector(s => s.orders.current);
  const disabled =
    addToCartBtnLoading ||
    cartFormSubmitBtnLoading ||
    getCurrentOrderLoading ||
    isRequiredFieldsLoading ||
    isOrderPriceAvailabilityLoading ||
    hasErrors ||
    isFormEmpty ||
    isCheckoutForCSRDisabled;

  const handleAddToCartAndCheckoutClick = useCallback(() => {
    onAddToCartClick(values, formikBag, true);
  }, [onAddToCartClick, values, formikBag]);
  const handleAddToCartClick = useCallback(() => {
    onAddToCartClick(values, formikBag);
  }, [onAddToCartClick, values, formikBag]);

  const getButtons = () => (
    <div className={cx('d-flex flex-column flex-md-row')}>
      <AlloyButton
        buttonType="secondary"
        disabled={disabled}
        className={cx({ 'mb-2': !isMD })}
        size="dynamic"
        onClick={handleAddToCartClick}
        type="submit"
      >
        {t('ADD_TO_CART')}
      </AlloyButton>
      {!!showAddToCartCheckout && (
        <AlloyButton
          buttonType="primary"
          className={cx('ms-md-3', {
            [`${styles['sticky-container__checkout-btn']}`]: !isMD
          })}
          disabled={disabled || (!currentOrderId && !!requestByDateEnabled)}
          size="dynamic"
          type="button"
          onClick={handleAddToCartAndCheckoutClick}
        >
          {t('ONECLICKBUY_')}
        </AlloyButton>
      )}
    </div>
  );

  return isMD ? (
    getButtons()
  ) : (
    <div className="mx-n3 bottom-0 position-sticky">
      <StickySection
        className={cx(
          'mt-5 p-3 align-items-center bg-white z-index-1 w-100',
          styles['sticky-container']
        )}
      >
        {getButtons()}
      </StickySection>
    </div>
  );
};

QuickOrderActionButtons.propTypes = {
  showAddToCartCheckout: PropTypes.bool,
  onAddToCartClick: PropTypes.func
};

export default QuickOrderActionButtons;
